// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Vaccine Decision Tree Template Generator
 * 
 * Purpose: Create pre-configured "Vaccinate vs No Vaccinate" tree structure
 * for MVP two-strategy comparison
 * 
 * This template follows the MVP requirement:
 * "Users can run deterministic computation for two-strategy comparison (Vaccinate vs. No Vaccinate)"
 */

import type { TreeNode, TreeEdge } from '../lib/validation';
import { useVaccineClientStore } from '../store/vaccineClientStore';

export interface VaccineTreeTemplate {
  rootId: string;
  nodes: Record<string, TreeNode>;
  edges: TreeEdge[];
}

export interface VaccineTemplateParams {
  vaccineCost?: number;
  treatmentCost?: number;
  efficacy?: number; // 0-1
  baselineRisk?: number; // 0-1
}

/**
 * Create a standard "Vaccinate vs No Vaccinate" decision tree
 * 
 * Structure:
 * Root (Decision) → Vaccinate/No Vaccinate
 *   ├─ Vaccinate (Chance) → Healthy/Infected
 *   │  ├─ Healthy (Terminal): Cost=vaccine, QALY=1.0
 *   │  └─ Infected (Terminal): Cost=vaccine+treatment, QALY=0.85
 *   └─ No Vaccinate (Chance) → Healthy/Infected
 *      ├─ Healthy (Terminal): Cost=0, QALY=1.0
 *      └─ Infected (Terminal): Cost=treatment, QALY=0.80
 */
export function createVaccineComparisonTree(
  params: VaccineTemplateParams = {}
): VaccineTreeTemplate {
  const {
    treatmentCost = 500,
    efficacy = 0.70,
    baselineRisk = 0.30, // 30% baseline infection risk without vaccine
  } = params;

  // Calculate probabilities
  const vaccineHealthyProb = 1 - (baselineRisk * (1 - efficacy));
  const vaccineInfectedProb = baselineRisk * (1 - efficacy);
  const noVaccineHealthyProb = 1 - baselineRisk;
  const noVaccineInfectedProb = baselineRisk; 

  const nodes: Record<string, TreeNode> = {
    root: {
      id: 'root',
      type: 'decision',
      label: 'Vaccination Strategy',
      cost: 0,
      qaly: 0,
      children: ['vaccinate', 'no_vaccinate'],
    },
    vaccinate: {
      id: 'vaccinate',
      type: 'chance',
      label: 'Vaccinate',
      cost: 0,
      qaly: 0,
      children: ['vac_healthy', 'vac_infected'],
    },
    no_vaccinate: {
      id: 'no_vaccinate',
      type: 'chance',
      label: 'No Vaccinate',
      cost: 0,
      qaly: 0,
      children: ['no_vac_healthy', 'no_vac_infected'],
    },
    vac_healthy: {
      id: 'vac_healthy',
      type: 'terminal',
      label: 'Healthy (Vaccinated)',
      cost: 0,
      qaly: 1.0,
      nodeType: 'healthy',
      children: [],
    },
    vac_infected: {
      id: 'vac_infected',
      type: 'terminal',
      label: 'Infected (Vaccinated)',
      cost: treatmentCost,
      qaly: 0.85,
      nodeType: 'infection',
      children: [],
    },
    no_vac_healthy: {
      id: 'no_vac_healthy',
      type: 'terminal',
      label: 'Healthy (Unvaccinated)',
      cost: 0,
      qaly: 1.0,
      nodeType: 'healthy',
      children: [],
    },
    no_vac_infected: {
      id: 'no_vac_infected',
      type: 'terminal',
      label: 'Infected (Unvaccinated)',
      cost: treatmentCost,
      qaly: 0.80,
      nodeType: 'infection',
      children: [],
    },
  };

  const edges: TreeEdge[] = [
    {
      parentId: 'root',
      childId: 'vaccinate',
      label: 'Vaccinate',
      probability: undefined, // Decision node - no probability
    },
    {
      parentId: 'root',
      childId: 'no_vaccinate',
      label: 'No Vaccinate',
      probability: undefined, // Decision node - no probability
    },
    {
      parentId: 'vaccinate',
      childId: 'vac_healthy',
      label: 'No Infection',
      probability: vaccineHealthyProb,
    },
    {
      parentId: 'vaccinate',
      childId: 'vac_infected',
      label: 'Breakthrough Infection',
      probability: vaccineInfectedProb,
    },
    {
      parentId: 'no_vaccinate',
      childId: 'no_vac_healthy',
      label: 'No Infection',
      probability: noVaccineHealthyProb,
    },
    {
      parentId: 'no_vaccinate',
      childId: 'no_vac_infected',
      label: 'Infected',
      probability: noVaccineInfectedProb,
    },
  ];

  return {
    rootId: 'root',
    nodes,
    edges,
  };
}

/**
 * Initialize decision tree with vaccine template
 * Automatically called when vaccine dashboard parameters change
 * Updates the vaccineClientStore with the generated tree
 */
export function initializeVaccineTree(
  vaccineInputs: {
    vaccineCostPerDoseEUR: number;
    treatmentCostPerCaseEUR: number;
    vaccineEfficacyPct: number;
    baselineIncidencePer100k: number;
  }
): void {
  // Generate tree structure
  const template = createVaccineComparisonTree({
    vaccineCost: vaccineInputs.vaccineCostPerDoseEUR,
    treatmentCost: vaccineInputs.treatmentCostPerCaseEUR,
    efficacy: vaccineInputs.vaccineEfficacyPct / 100,
    baselineRisk: vaccineInputs.baselineIncidencePer100k / 100000,
  });
  
  // Update store with generated tree
  const store = useVaccineClientStore.getState();
  
  // CRITICAL: Don't clear if tree already has nodes - user may have edited it
  const hasExistingNodes = Object.keys(store.nodes).length > 0;
  if (hasExistingNodes) {
    // Tree already exists, don't reset it
    return;
  }
  
  // Clear and initialize only if tree is empty
  store.clearTree();
  
  // Add all nodes
  Object.values(template.nodes).forEach(node => {
    store.addNode(node);
  });
  
  // Add all edges
  template.edges.forEach(edge => {
    store.addEdge(edge);
  });
  
  // Set root
  store.setRootId(template.rootId);
}

/**
 * Check if current tree is empty or needs initialization
 */
export function needsTreeInitialization(nodes: Record<string, TreeNode>): boolean {
  return !nodes || Object.keys(nodes).length === 0;
}

/**
 * Validate that tree follows vaccine comparison structure
 * Returns true if tree has required nodes for two-strategy comparison
 */
export function isValidVaccineTree(nodes: Record<string, TreeNode>): boolean {
  if (!nodes || Object.keys(nodes).length === 0) {
    return false;
  }

  // Check for required nodes
  const requiredNodes = ['root', 'vaccinate', 'no_vaccinate'];
  const hasRequired = requiredNodes.every(nodeId => nodes[nodeId]);

  if (!hasRequired) {
    return false;
  }

  // Check root is decision type
  if (nodes.root.type !== 'decision') {
    return false;
  }

  // Check strategies are chance types
  if (nodes.vaccinate?.type !== 'chance' || nodes.no_vaccinate?.type !== 'chance') {
    return false;
  }

  return true;
}
